/**
 * Gigify Debug Utility
 * 
 * Provides standardized, controllable debugging functionality across the application.
 * Can be enabled/disabled via localStorage or environment variables.
 */

// Determine if debugging is enabled (check both localStorage and env variables)
const isDebugEnabled = () => {
    return process.env.REACT_APP_DEBUG_MODE === 'true' || 
           localStorage.getItem('gigifyDebugMode') === 'true';
  };
  
  // Severity levels for debugging
  const LEVELS = {
    INFO: { name: 'INFO', color: '#0066ff', emoji: 'ℹ️' },
    WARN: { name: 'WARN', color: '#ff9900', emoji: '⚠️' },
    ERROR: { name: 'ERROR', color: '#ff0000', emoji: '🛑' },
    SUCCESS: { name: 'SUCCESS', color: '#00A86B', emoji: '✅' },
    FIREBASE: { name: 'FIREBASE', color: '#FFA000', emoji: '🔥' },
    ROUTER: { name: 'ROUTER', color: '#9c27b0', emoji: '🔄' },
    AUTH: { name: 'AUTH', color: '#00A86B', emoji: '🔐' },
    RENDER: { name: 'RENDER', color: '#2196F3', emoji: '🎨' },
    API: { name: 'API', color: '#E91E63', emoji: '📡' }
  };
  
  // Get timestamp for logs
  const getTimestamp = () => {
    return new Date().toISOString().replace('T', ' ').substring(0, 19);
  };
  
  // Create module style for component logs
  const getModuleTag = (module) => {
    return module ? `[${module}]` : '';
  };
  
  // Base debug logger function with color formatting
  const logWithLevel = (level, module, message, data) => {
    if (!isDebugEnabled()) return;
  
    const timestamp = getTimestamp();
    const moduleTag = getModuleTag(module);
    
    // Basic console styling
    console.log(
      `%c ${timestamp} ${level.emoji} ${level.name} ${moduleTag}`,
      `color: ${level.color}; font-weight: bold;`,
      message,
      data !== undefined ? data : ''
    );
  };
  
  // Individual log functions for different levels
  const debug = {
    // Enable/disable debug mode programmatically
    enable: () => localStorage.setItem('gigifyDebugMode', 'true'),
    disable: () => localStorage.setItem('gigifyDebugMode', 'false'),
    isEnabled: isDebugEnabled,
    
    // General logging
    log: (module, message, data) => logWithLevel(LEVELS.INFO, module, message, data),
    warn: (module, message, data) => logWithLevel(LEVELS.WARN, module, message, data),
    error: (module, message, data) => {
      logWithLevel(LEVELS.ERROR, module, message, data);
      // Also log the error stack if available
      if (data instanceof Error && data.stack) {
        console.error(data.stack);
      }
    },
    success: (module, message, data) => logWithLevel(LEVELS.SUCCESS, module, message, data),
    
    // Domain-specific logging
    firebase: (module, message, data) => logWithLevel(LEVELS.FIREBASE, module, message, data),
    router: (module, message, data) => logWithLevel(LEVELS.ROUTER, module, message, data),
    auth: (module, message, data) => logWithLevel(LEVELS.AUTH, module, message, data),
    render: (module, message, data) => logWithLevel(LEVELS.RENDER, module, message, data),
    api: (module, message, data) => logWithLevel(LEVELS.API, module, message, data),
    
    // Performance monitoring
    time: (label) => {
      if (!isDebugEnabled()) return;
      console.time(label);
    },
    timeEnd: (label) => {
      if (!isDebugEnabled()) return;
      console.timeEnd(label);
    },
    
    // Group related logs
    group: (module, label, fn) => {
      if (!isDebugEnabled()) return;
      const moduleTag = getModuleTag(module);
      console.group(`${moduleTag} ${label}`);
      fn();
      console.groupEnd();
    },
    
    // Object inspection
    table: (data, columns) => {
      if (!isDebugEnabled()) return;
      console.table(data, columns);
    },
    
    // Special logger for component lifecycle in React
    component: {
      mount: (componentName) => logWithLevel(LEVELS.RENDER, componentName, 'Component mounted'),
      update: (componentName, props, prevProps) => logWithLevel(LEVELS.RENDER, componentName, 'Component updated', { props, prevProps }),
      unmount: (componentName) => logWithLevel(LEVELS.RENDER, componentName, 'Component unmounted')
    }
  };
  
  export default debug;
  